import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchQuickQuotes } from "../redux/features/quickQuateSlice";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format } from "date-fns";
import _ from "lodash";

// Colors for service types
const SERVICE_COLORS = {
  commercial_cleaning: "#4F46E5",
  residential_cleaning: "#10B981",
  industrial_cleaning: "#F59E0B",
  specialized_cleaning: "#EF4444",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    quotes = [],
    loading,
    error,
  } = useSelector((state) => state.quickQuote);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calculate pagination
  const indexOfLastQuote = currentPage * itemsPerPage;
  const indexOfFirstQuote = indexOfLastQuote - itemsPerPage;
  const currentQuotes = quotes.slice(indexOfFirstQuote, indexOfLastQuote);
  const totalPages = Math.ceil(quotes.length / itemsPerPage);

  useEffect(() => {
    dispatch(fetchQuickQuotes());
  }, [dispatch]);

  // Helper function to format service type names
  const formatServiceType = (type) => {
    return type
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Calculate statistics for all service types
  const serviceStats = useMemo(() => {
    const stats = {
      commercial_cleaning: 0,
      residential_cleaning: 0,
      industrial_cleaning: 0,
      specialized_cleaning: 0,
    };

    quotes.forEach((quote) => {
      if (stats.hasOwnProperty(quote.serviceType)) {
        stats[quote.serviceType]++;
      }
    });

    return stats;
  }, [quotes]);

  // Prepare data for pie chart
  const serviceTypeData = useMemo(() => {
    if (!quotes?.length) return [];

    return Object.entries(serviceStats)
      .map(([type, count]) => ({
        name: formatServiceType(type),
        value: count,
        color: SERVICE_COLORS[type],
      }))
      .filter((item) => item.value > 0); // Only include services with values > 0
  }, [serviceStats, quotes]);

  // Prepare data for line chart
  const monthlyChartData = useMemo(() => {
    if (!Array.isArray(quotes) || quotes.length === 0) return [];

    const monthlyGroups = _.groupBy(quotes, () =>
      format(new Date(), "yyyy-MM")
    );

    return Object.entries(monthlyGroups)
      .map(([month, monthQuotes]) => {
        const serviceCounts = _.countBy(monthQuotes, "serviceType");
        return {
          month,
          total: monthQuotes.length,
          commercial: serviceCounts.commercial_cleaning || 0,
          residential: serviceCounts.residential_cleaning || 0,
          industrial: serviceCounts.industrial_cleaning || 0,
          specialized: serviceCounts.specialized_cleaning || 0,
        };
      })
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [quotes]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );

  if (error)
    return (
      <div className="p-4 text-red-600 bg-red-50 rounded-lg">
        Error: {error}
      </div>
    );

  const handleView = (id) => {
    navigate(`/quick-quotes/${id}`);
  };

  const Pagination = () => {
    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{indexOfFirstQuote + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(indexOfLastQuote, quotes.length)}
              </span>{" "}
              of <span className="font-medium">{quotes.length}</span> results
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <div className="flex space-x-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    currentPage === index + 1
                      ? "z-10 bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Calendar Button */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <button
          onClick={() => navigate("/calendar")}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Calendar View
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(serviceStats).map(([type, count]) => (
          <div key={type} className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-700">
              {formatServiceType(type)}
            </h3>
            <p
              className="text-3xl font-bold"
              style={{ color: SERVICE_COLORS[type] }}
            >
              {count}
            </p>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Service Type Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Monthly Service Distribution
          </h2>
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tickFormatter={(dateStr) =>
                    format(new Date(dateStr), "MMM yyyy")
                  }
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => [
                    value,
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]}
                  labelFormatter={(dateStr) =>
                    format(new Date(dateStr), "MMMM yyyy")
                  }
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke="#312E81"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="commercial"
                  name="Commercial"
                  stroke={SERVICE_COLORS.commercial_cleaning}
                />
                <Line
                  type="monotone"
                  dataKey="residential"
                  name="Residential"
                  stroke={SERVICE_COLORS.residential_cleaning}
                />
                <Line
                  type="monotone"
                  dataKey="industrial"
                  name="Industrial"
                  stroke={SERVICE_COLORS.industrial_cleaning}
                />
                <Line
                  type="monotone"
                  dataKey="specialized"
                  name="Specialized"
                  stroke={SERVICE_COLORS.specialized_cleaning}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Type Distribution Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Service Type Distribution
          </h2>
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={serviceTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={140}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  {serviceTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick Quotes Table */}
      {/* Quick Quotes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Quick Quotes List
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentQuotes.map((quote) => (
                <tr key={quote.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {quote.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{quote.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {quote.mobileNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                      style={{
                        backgroundColor: `${
                          SERVICE_COLORS[quote.serviceType]
                        }20`,
                        color: SERVICE_COLORS[quote.serviceType],
                      }}
                    >
                      {formatServiceType(quote.serviceType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(quote.id)}
                      className="text-blue-600 hover:text-blue-900 ml-4 flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path
                          fillRule="evenodd"
                          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination />
        </div>
      </div>
    </div>
  );
}
